import { Delete, ExpandMore } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, CircularProgress, DialogContentText, Grid, IconButton, List, ListItem, ListItemText, Snackbar } from "@mui/material";
import { cloneDeep } from "lodash";
import React, { useEffect, useState } from "react";
import { } from "reactstrap";
import Api from "../../Api";
import { IComboBoxOption, IMaterialDescriptionComboBoxOption, IMaterialSiteComboBoxOption } from "../../interfaces";
import { Values } from "../../utils";
import { AddValueDialog } from "./AddValueDialog";
import { DeleteValueDialog } from "./DeleteValueDialog";
import './index.scss';



interface IValueSettingModel {
    entityId?: string,
    type: string,
    nested?: string,
    current: IComboBoxOption[] | IMaterialSiteComboBoxOption[] | IMaterialDescriptionComboBoxOption[],
    setFunction: React.Dispatch<React.SetStateAction<IComboBoxOption[]>>
    action: "add" | "remove"
}


export const ValueManager = () => {

    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState("");
    const [nested, setNested] = useState("");
    const [currentAction, setCurrentAction] = useState<IValueSettingModel>();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const [spareTypes, setSpareTypes] = useState<IComboBoxOption[]>([]);
    const [storageTypes, setStorageTypes] = useState<IComboBoxOption[]>([]);

    const [vehicleClasses, setVehicleClasses] = useState<IComboBoxOption[]>([]);
    const [sites, setSites] = useState<IMaterialSiteComboBoxOption[]>([]);
    const [materialStates, setMaterialStates] = useState<IComboBoxOption[]>([]);
    const [expectedMaterialReturnStates, setExpectedMaterialReturnStates] = useState<IComboBoxOption[]>([]);
    const [uoms, setUoms] = useState<IComboBoxOption[]>([]);
    const [l1s, setL1s] = useState<IMaterialDescriptionComboBoxOption[]>([]);

    const [contractmanagers, setContractManagers] = useState<IComboBoxOption[]>([]);
    const [categorymanagers, setCategoryManagers] = useState<IComboBoxOption[]>([]);

    useEffect(() => {

        if (loading) {
            let requiredValues = [
                ["sparetypes", setSpareTypes],
                ["storagetypes", setStorageTypes],
                ["vehicleclasses", setVehicleClasses],
                ["sites", setSites],
                ["materialstates", setMaterialStates],
                ["expectedmaterialreturnstates", setExpectedMaterialReturnStates],
                ["uoms", setUoms],
                ["descriptions", setL1s],
                ["contract-managers", setContractManagers],
                ["category-managers", setCategoryManagers]
            ];

            requiredValues.forEach(x => Values.setValues(x[0] as string, x[1]));

            setLoading(false);
        }

    }, [loading]);


    const handleExpand = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : "");
        setNested("");
    };

    const handleNested = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setNested(isExpanded ? panel : "");
    };

    const handleUpdate = (model: IValueSettingModel) => {
        setCurrentAction(model);
    }

    const onAdd = (value: string) => {
        if (currentAction) {

            let endpoint = currentAction.nested ?
                `${currentAction.type}/${currentAction.nested}` :
                currentAction.type;

            //setLoading(true);

            Api.Values.add(endpoint, value).then((data: any) => {

                setSuccessMessage("added value");

                if (data.id) {

                    let newValues = cloneDeep(currentAction.current);

                    if (currentAction.nested) {

                        if (currentAction.type === "sites") {
                            (newValues as IMaterialSiteComboBoxOption[]).forEach(x => {
                                if (x.id === currentAction.nested) {

                                    let newNestedValue: IComboBoxOption = {
                                        id: data.id as string,
                                        value
                                    };

                                    if (x.getWarehouses) x.getWarehouses.push(newNestedValue);
                                    else x.getWarehouses = [newNestedValue]
                                }
                            })
                        } else if (currentAction.type === "descriptions") {
                            (newValues as IMaterialDescriptionComboBoxOption[]).forEach(x => {
                                if (x.id === currentAction.nested) {

                                    let newNestedValue: IComboBoxOption = {
                                        id: data.id as string,
                                        value
                                    };

                                    if (x.getL2Descriptions) x.getL2Descriptions.push(newNestedValue);
                                    else x.getL2Descriptions = [newNestedValue]
                                }
                            })
                        }

                    } else newValues.push({
                        id: data.id as string,
                        value
                    });

                    currentAction.setFunction(newValues);
                }
            }).catch(() => {
                setErrorMessage("failed to add value");
            }).finally(() => {
                setLoading(false);
            })
        }

        setCurrentAction(undefined);
    }

    const onRemove = () => {

        if (currentAction && currentAction.entityId) {

            let endpoint = currentAction.nested ?
                `${currentAction.type}/${currentAction.nested}` :
                currentAction.type;

            // setLoading(true);

            Api.Values.remove(endpoint, currentAction.entityId).then(() => {
                setSuccessMessage("removed value");
            }).catch(() => {
                setErrorMessage("failed to remove value");
            }).finally(() => {
                setLoading(false);
            });

            let newValues: IComboBoxOption[] | IMaterialSiteComboBoxOption[] | IMaterialDescriptionComboBoxOption[] = cloneDeep(currentAction.current);

            if (currentAction.nested) {
                if (currentAction.type === "sites") {
                    (newValues as IMaterialSiteComboBoxOption[]).forEach(val => {
                        if (val.getWarehouses)
                            val.getWarehouses = val.getWarehouses.filter(x => x.id !== currentAction.entityId)
                    })
                }
                else if (currentAction.type === "descriptions") {
                    (newValues as IMaterialDescriptionComboBoxOption[]).forEach(val => {
                        if (val.getL2Descriptions)
                            val.getL2Descriptions = val.getL2Descriptions.filter(x => x.id !== currentAction.entityId)
                    })
                }
            } else newValues = newValues.filter(x => x.id !== currentAction.entityId);

            currentAction.setFunction(newValues);
        }

        setCurrentAction(undefined);
    }


    const getCurrentEntityName = () => {

        let entityName = "unknown entity";

        if (!currentAction) return entityName;

        if (currentAction.nested) {
            if (currentAction.type === "sites") {
                (currentAction.current as IMaterialSiteComboBoxOption[]).forEach(x => {
                    if (x.getWarehouses)
                        x.getWarehouses.forEach(w => {
                            if (w.id === currentAction.entityId)
                                entityName = w.value ?? entityName;
                        })
                })
            } else if (currentAction.type === "descriptions") {
                (currentAction.current as IMaterialDescriptionComboBoxOption[]).forEach(x => {
                    if (x.getL2Descriptions)
                        x.getL2Descriptions.forEach(w => {
                            if (w.id === currentAction.entityId)
                                entityName = w.value ?? entityName;
                        })
                })
            }
        } else {
            entityName = currentAction.current
                .filter(x => x.id === currentAction.entityId)[0].value ?? entityName;
        }

        return entityName;
    }

    return (
        <>
            {loading ? <Box textAlign="center" sx={{ opacity: .4, mt: 10 }}><CircularProgress /></Box> :

                <Grid item xs={12}>

                    <Accordion
                        expanded={expanded === "SpareType"}
                        onChange={handleExpand("SpareType")}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMore />}
                        >
                            <DialogContentText color="primary">
                                Spare Types
                            </DialogContentText>
                        </AccordionSummary>
                        <AccordionDetails>
                            <List>
                                {spareTypes.map(lov => {
                                    return <ListItem key={lov.id}
                                        sx={{ borderBottom: '1px solid lightgrey' }}
                                        secondaryAction={
                                            <IconButton
                                                edge="end"
                                                aria-label="delete"
                                                onClick={() => handleUpdate({
                                                    entityId: lov.id ?? undefined,
                                                    type: "sparetypes",
                                                    current: spareTypes,
                                                    setFunction: setSpareTypes,
                                                    action: "remove"
                                                })}
                                            >
                                                <Delete />
                                            </IconButton>}
                                    >
                                        <ListItemText
                                            primary={<span>{lov.value}</span>}

                                        />
                                    </ListItem>
                                })}

                            </List>
                            <Button
                                variant="contained"
                                onClick={() => handleUpdate({
                                    type: "sparetypes",
                                    current: spareTypes,
                                    setFunction: setSpareTypes,
                                    action: "add"
                                })}
                            >New Value</Button>
                        </AccordionDetails>
                    </Accordion>
                    <Accordion
                        expanded={expanded === "StorageType"}
                        onChange={handleExpand("StorageType")}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMore />}
                        >
                            <DialogContentText color="primary">
                                Storage Types
                            </DialogContentText>
                        </AccordionSummary>
                        <AccordionDetails>
                            <List>
                                {storageTypes.map(lov => {
                                    return <ListItem key={lov.id}
                                        sx={{ borderBottom: '1px solid lightgrey' }}
                                        secondaryAction={
                                            <IconButton
                                                edge="end"
                                                aria-label="delete"
                                                onClick={() => handleUpdate({
                                                    entityId: lov.id ?? undefined,
                                                    type: "storagetypes",
                                                    current: storageTypes,
                                                    setFunction: setStorageTypes,
                                                    action: "remove"
                                                })}
                                            >
                                                <Delete />
                                            </IconButton>}
                                    >
                                        <ListItemText
                                            primary={<span>{lov.value}</span>}

                                        />
                                    </ListItem>
                                })}

                            </List>
                            <Button
                                variant="contained"
                                onClick={() => handleUpdate({
                                    type: "storagetypes",
                                    current: storageTypes,
                                    setFunction: setStorageTypes,
                                    action: "add"
                                })}
                            >New Value</Button>
                        </AccordionDetails>
                    </Accordion>
                    <Accordion
                        expanded={expanded === "VehicleClasses"}
                        onChange={handleExpand("VehicleClasses")}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMore />}
                        >
                            <DialogContentText color="primary">
                                Vehicle Classes
                            </DialogContentText>
                        </AccordionSummary>
                        <AccordionDetails>
                            <List>
                                {vehicleClasses.map(lov => {
                                    return <ListItem key={lov.id}
                                        sx={{ borderBottom: '1px solid lightgrey' }}
                                        secondaryAction={
                                            <IconButton
                                                edge="end"
                                                aria-label="delete"
                                                onClick={() => handleUpdate({
                                                    entityId: lov.id ?? undefined,
                                                    type: "vehicleclasses",
                                                    current: vehicleClasses,
                                                    setFunction: setVehicleClasses,
                                                    action: "remove"
                                                })}
                                            >
                                                <Delete />
                                            </IconButton>}
                                    >
                                        <ListItemText
                                            primary={<span>{lov.value}</span>}

                                        />
                                    </ListItem>
                                })}
                            </List>
                            <Button
                                variant="contained"
                                onClick={() => handleUpdate({
                                    type: "vehicleclasses",
                                    current: vehicleClasses,
                                    setFunction: setVehicleClasses,
                                    action: "add"
                                })}
                            >New Value</Button>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion
                        expanded={expanded === "Sites"}
                        onChange={handleExpand("Sites")}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMore />}
                        >
                            <DialogContentText color="primary">
                                Sites & Warehouses
                            </DialogContentText>
                        </AccordionSummary>
                        <AccordionDetails>
                            <List >
                                {sites.map(lov => {
                                    return <><ListItem key={lov.id}
                                        sx={{ borderBottom: '1px solid lightgrey' }}
                                        secondaryAction={!nested.startsWith("Warehouse_") &&
                                            <IconButton
                                                edge="end"
                                                aria-label="delete"
                                                onClick={() => handleUpdate({
                                                    entityId: lov.id ?? undefined,
                                                    type: "sites",
                                                    current: sites,
                                                    setFunction: setSites,
                                                    action: "remove"
                                                })}
                                            >
                                                <Delete />
                                            </IconButton>}
                                    >
                                        <ListItemText
                                            primary={

                                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                                    <span>{lov.value}</span>


                                                    <Accordion
                                                        expanded={nested === "Warehouse_" + lov.id}
                                                        onChange={handleNested("Warehouse_" + lov.id)}
                                                        sx={{ display: "inline", width: "80%" }}
                                                    >
                                                        <AccordionSummary
                                                            expandIcon={<ExpandMore />}
                                                        >
                                                            <DialogContentText variant="caption" color="primary">
                                                                <strong>
                                                                    Warehouses
                                                                </strong>
                                                                <small>{(nested === "Warehouse_" + lov.id) ? " (click to hide)" : " (click to expand)"}</small>
                                                            </DialogContentText>
                                                        </AccordionSummary>
                                                        <AccordionDetails>
                                                            <List component="div" >
                                                                {lov.getWarehouses?.map(warehouse => {
                                                                    return <ListItem key={warehouse.id}
                                                                        sx={{ px: 5, borderBottom: '1px solid lightgrey' }}
                                                                        secondaryAction={
                                                                            <IconButton
                                                                                edge="end"
                                                                                aria-label="delete"
                                                                                onClick={() => handleUpdate({
                                                                                    entityId: warehouse.id ?? undefined,
                                                                                    type: "sites",
                                                                                    nested: lov.id ?? undefined,
                                                                                    current: sites,
                                                                                    setFunction: setSites,
                                                                                    action: "remove"
                                                                                })}
                                                                            >
                                                                                <Delete />
                                                                            </IconButton>}
                                                                    >
                                                                        <ListItemText
                                                                            primary={<span>{warehouse.value}</span>}

                                                                        />
                                                                    </ListItem>
                                                                })}
                                                            </List>
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                onClick={() => handleUpdate({
                                                                    type: "sites",
                                                                    nested: lov.id ?? undefined,
                                                                    current: sites,
                                                                    setFunction: setSites,
                                                                    action: "add"
                                                                })}
                                                            >New Value</Button>
                                                        </AccordionDetails>
                                                    </Accordion>
                                                </Box>
                                            }
                                        />
                                    </ListItem>

                                    </>
                                })}

                            </List>
                            <Button
                                variant="contained"
                                onClick={() => handleUpdate({
                                    type: "sites",
                                    current: sites,
                                    setFunction: setSites,
                                    action: "add"
                                })}
                            >New Value</Button>
                        </AccordionDetails>

                    </Accordion>

                    <Accordion
                        expanded={expanded === "MaterialStates"}
                        onChange={handleExpand("MaterialStates")}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMore />}
                        >
                            <DialogContentText color="primary">
                                Material Status
                            </DialogContentText>
                        </AccordionSummary>
                        <AccordionDetails>
                            <List>
                                {materialStates.map(lov => {
                                    return <ListItem key={lov.id}
                                        sx={{ borderBottom: '1px solid lightgrey' }}
                                        secondaryAction={
                                            <IconButton
                                                edge="end"
                                                aria-label="delete"
                                                onClick={() => handleUpdate({
                                                    entityId: lov.id ?? undefined,
                                                    type: "materialstates",
                                                    current: materialStates,
                                                    setFunction: setMaterialStates,
                                                    action: "remove"
                                                })}
                                            >
                                                <Delete />
                                            </IconButton>}
                                    >
                                        <ListItemText
                                            primary={<span>{lov.value}</span>}

                                        />
                                    </ListItem>
                                })}
                            </List>
                            <Button
                                variant="contained"
                                onClick={() => handleUpdate({
                                    type: "materialstates",
                                    current: materialStates,
                                    setFunction: setMaterialStates,
                                    action: "add"
                                })}
                            >New Value</Button>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion
                        expanded={expanded === "ExpectedMaterialReturnStates"}
                        onChange={handleExpand("ExpectedMaterialReturnStates")}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMore />}
                        >
                            <DialogContentText color="primary">
                                Expected Material Return Status
                            </DialogContentText>
                        </AccordionSummary>
                        <AccordionDetails>
                            <List>
                                {expectedMaterialReturnStates.map(lov => {
                                    return <ListItem key={lov.id}
                                        sx={{ borderBottom: '1px solid lightgrey' }}
                                        secondaryAction={
                                            <IconButton
                                                edge="end"
                                                aria-label="delete"
                                                onClick={() => handleUpdate({
                                                    entityId: lov.id ?? undefined,
                                                    type: "expectedmaterialreturnstates",
                                                    current: expectedMaterialReturnStates,
                                                    setFunction: setExpectedMaterialReturnStates,
                                                    action: "remove"
                                                })}
                                            >
                                                <Delete />
                                            </IconButton>}
                                    >
                                        <ListItemText
                                            primary={<span>{lov.value}</span>}

                                        />
                                    </ListItem>
                                })}

                            </List>
                            <Button
                                variant="contained"
                                onClick={() => handleUpdate({
                                    type: "expectedmaterialreturnstates",
                                    current: expectedMaterialReturnStates,
                                    setFunction: setExpectedMaterialReturnStates,
                                    action: "add"
                                })}
                            >New Value</Button>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion
                        expanded={expanded === "UOMs"}
                        onChange={handleExpand("UOMs")}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMore />}
                        >
                            <DialogContentText color="primary">
                                Units of Measurement
                            </DialogContentText>
                        </AccordionSummary>
                        <AccordionDetails>
                            <List>
                                {uoms.map(lov => {
                                    return <ListItem key={lov.id}
                                        sx={{ borderBottom: '1px solid lightgrey' }}
                                        secondaryAction={
                                            <IconButton
                                                edge="end"
                                                aria-label="delete"
                                                onClick={() => handleUpdate({
                                                    entityId: lov.id ?? undefined,
                                                    type: "uoms",
                                                    current: uoms,
                                                    setFunction: setUoms,
                                                    action: "remove"
                                                })}
                                            >
                                                <Delete />
                                            </IconButton>}
                                    >
                                        <ListItemText
                                            primary={<span>{lov.value}</span>}

                                        />
                                    </ListItem>
                                })}
                            </List>
                            <Button
                                variant="contained"
                                onClick={() => handleUpdate({
                                    type: "uoms",
                                    current: uoms,
                                    setFunction: setUoms,
                                    action: "add"
                                })}
                            >New Value</Button>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion
                        expanded={expanded === "L1s"}
                        onChange={handleExpand("L1s")}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMore />}
                        >
                            <DialogContentText color="primary">
                                Descriptions (L1 / L2)
                            </DialogContentText>
                        </AccordionSummary>
                        <AccordionDetails>
                            <List>
                                {l1s.map(lov => {
                                    return <ListItem key={lov.id}
                                        sx={{ borderBottom: '1px solid lightgrey' }}
                                        secondaryAction={
                                            !nested.startsWith("L2_") &&
                                            <IconButton
                                                edge="end"
                                                aria-label="delete"
                                                onClick={() => handleUpdate({
                                                    entityId: lov.id ?? undefined,
                                                    type: "descriptions",
                                                    current: l1s,
                                                    setFunction: setL1s,
                                                    action: "remove"
                                                })}
                                            >
                                                <Delete />
                                            </IconButton>}
                                    >
                                        <ListItemText
                                            primary={

                                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                                    <span style={{ width: "20%" }}>{lov.value}</span>


                                                    <Accordion
                                                        expanded={nested === "L2_" + lov.id}
                                                        onChange={handleNested("L2_" + lov.id)}
                                                        sx={{ display: "inline", width: "80%" }}
                                                    >
                                                        <AccordionSummary
                                                            expandIcon={<ExpandMore />}
                                                        >
                                                            <DialogContentText variant="caption" color="primary">
                                                                <strong>
                                                                    Level 2 Descriptions
                                                                </strong>
                                                                <small>{(nested === "L2_" + lov.id) ? " (click to hide)" : " (click to expand)"}</small>
                                                            </DialogContentText>
                                                        </AccordionSummary>
                                                        <AccordionDetails>
                                                            <List component="div" >
                                                                {lov.getL2Descriptions?.map(l2 => {
                                                                    return <ListItem key={l2.id}
                                                                        sx={{ px: 5, borderBottom: '1px solid lightgrey' }}
                                                                        secondaryAction={
                                                                            <IconButton
                                                                                edge="end"
                                                                                aria-label="delete"
                                                                                onClick={() => handleUpdate({
                                                                                    entityId: l2.id ?? undefined,
                                                                                    type: "descriptions",
                                                                                    nested: lov.id ?? undefined,
                                                                                    current: l1s,
                                                                                    setFunction: setL1s,
                                                                                    action: "remove"
                                                                                })}
                                                                            >
                                                                                <Delete />
                                                                            </IconButton>}
                                                                    >
                                                                        <ListItemText
                                                                            primary={<span>{l2.value}</span>}

                                                                        />
                                                                    </ListItem>
                                                                })}
                                                            </List>
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                onClick={() => handleUpdate({
                                                                    type: "descriptions",
                                                                    nested: lov.id ?? undefined,
                                                                    current: l1s,
                                                                    setFunction: setL1s,
                                                                    action: "add"
                                                                })}
                                                            >New Value</Button>
                                                        </AccordionDetails>
                                                    </Accordion>
                                                </Box>
                                            }

                                        />
                                    </ListItem>
                                })}
                            </List>
                            <Button
                                variant="contained"
                                onClick={() => handleUpdate({
                                    type: "descriptions",
                                    current: l1s,
                                    setFunction: setL1s,
                                    action: "add"
                                })}
                            >New Value</Button>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion
                        expanded={expanded === "CategoryManagers"}
                        onChange={handleExpand("CategoryManagers")}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMore />}
                        >
                            <DialogContentText color="primary">
                                Commercial Managers
                            </DialogContentText>
                        </AccordionSummary>
                        <AccordionDetails>
                            <List>
                                {categorymanagers.map(lov => {
                                    return <ListItem key={lov.id}
                                        sx={{ borderBottom: '1px solid lightgrey' }}
                                        secondaryAction={
                                            <IconButton
                                                edge="end"
                                                aria-label="delete"
                                                onClick={() => handleUpdate({
                                                    entityId: lov.id ?? undefined,
                                                    type: "category-managers",
                                                    current: categorymanagers,
                                                    setFunction: setCategoryManagers,
                                                    action: "remove"
                                                })}
                                            >
                                                <Delete />
                                            </IconButton>}
                                    >
                                        <ListItemText
                                            primary={<span>{lov.value}</span>}

                                        />
                                    </ListItem>
                                })}
                            </List>
                            <Button
                                variant="contained"
                                onClick={() => handleUpdate({
                                    type: "category-managers",
                                    current: categorymanagers,
                                    setFunction: setCategoryManagers,
                                    action: "add"
                                })}
                            >New Value</Button>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion
                        expanded={expanded === "ContractManagers"}
                        onChange={handleExpand("ContractManagers")}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMore />}
                        >
                            <DialogContentText color="primary">
                                Procurement Managers
                            </DialogContentText>
                        </AccordionSummary>
                        <AccordionDetails>
                            <List>
                                {contractmanagers.map(lov => {
                                    return <ListItem key={lov.id}
                                        sx={{ borderBottom: '1px solid lightgrey' }}
                                        secondaryAction={
                                            <IconButton
                                                edge="end"
                                                aria-label="delete"
                                                onClick={() => handleUpdate({
                                                    entityId: lov.id ?? undefined,
                                                    type: "contract-managers",
                                                    current: contractmanagers,
                                                    setFunction: setContractManagers,
                                                    action: "remove"
                                                })}
                                            >
                                                <Delete />
                                            </IconButton>}
                                    >
                                        <ListItemText
                                            primary={<span>{lov.value}</span>}

                                        />
                                    </ListItem>
                                })}

                            </List>
                            <Button
                                variant="contained"
                                onClick={() => handleUpdate({
                                    type: "contract-managers",
                                    current: contractmanagers,
                                    setFunction: setContractManagers,
                                    action: "add"
                                })}
                            >New Value</Button>
                        </AccordionDetails>
                    </Accordion>


                    <Snackbar
                        sx={{ mt: 10 }}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        open={errorMessage !== null}
                        autoHideDuration={6000}
                        onClose={() => setErrorMessage(null)}>
                        <Alert severity="error">{errorMessage}</Alert>
                    </Snackbar>

                    <Snackbar
                        sx={{ mt: 10 }}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        open={successMessage !== null}
                        autoHideDuration={6000}
                        onClose={() => setSuccessMessage(null)}>
                        <Alert severity="success">{successMessage}</Alert>
                    </Snackbar>


                    {(currentAction && currentAction.action === "add") &&
                        <AddValueDialog
                            open={currentAction.action === "add"}
                            handleClose={(value) => { value ? onAdd(value) : setCurrentAction(undefined) }}
                        />
                    }


                    {(currentAction && currentAction.action === "remove") &&
                        <DeleteValueDialog
                            message={`Remove "${getCurrentEntityName()}" from the list of values?`}
                            open={currentAction.action === "remove"}
                            handleClose={(confirm) => { confirm ? onRemove() : setCurrentAction(undefined) }}
                        />
                    }


                </Grid>

            }


        </>
    );

}